<template>
  <div
    class="opener"
    :class="[phase, `mood-${current?.rarity ?? 'common'}`, stowKind]"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="onPointerUp"
    @pointercancel="onPointerUp"
  >
    <div class="vignette" />
    <div v-if="flashing" class="flash" :class="current.rarity" />

    <p v-if="showPackProgress" class="pack-progress">
      {{ t('packs.packOf', { n: batchIndex + 1, total: batches.length }) }}
    </p>
    <div class="hud">
      <button type="button" class="sfx" @pointerdown.stop @click="toggleSfx">
        {{ muted ? t('packs.sfxOff') : t('packs.sfxOn') }}
      </button>
      <button
        v-if="phase !== 'summary' && !(phase === 'stow' && stowDone)"
        type="button"
        class="skip"
        @pointerdown.stop
        @click="skip"
      >
        {{ t('packs.skip') }}
      </button>
    </div>
    <div
      v-if="showSideStack"
      ref="sideStackHost"
      class="side-stack-host"
    />

    <div v-if="phase === 'tear'" class="tear-stage">
      <p class="hint">{{ t('packs.tearHint') }}</p>
      <div ref="tearHost" class="tear-host" />
      <p class="pack-name">{{ pack.name }}</p>
      <button
        type="button"
        class="ghost-open"
        :disabled="!inputReady"
        @pointerdown.stop
        @click="finishTear"
      >
        {{ t('packs.openBtn') }}
      </button>
    </div>

    <div v-else-if="phase === 'burst'" class="burst-stage">
      <img
        v-for="card in burstCards"
        :key="card.i"
        class="burst-card"
        :style="card.style"
        :src="cardBack"
        alt=""
      />
    </div>

    <div v-else-if="phase === 'reveal'" class="reveal-stage">
      <p class="count">{{ index + 1 }} / {{ pulls.length }}</p>
      <div class="stack" aria-hidden="true">
        <span v-for="n in stackCount" :key="n" :style="{ '--s': n }" />
      </div>
      <div
        :key="revealCardKey"
        class="card-stage"
        :class="[
          `foil-${current.rarity}`,
          {
            flipped,
            entering,
            exiting,
            shake: shaking,
            last: isLast,
          },
        ]"
      >
        <div class="card-3d" :class="{ flipped }">
          <div class="face back">
            <img :src="cardBack" alt="" />
          </div>
          <div class="face front">
            <YugiohCard
              :key="revealCardKey"
              :card="current.card"
              size="large"
              fluid
              :clickable="false"
            />
            <div class="foil-sweep" />
            <span v-if="current.isNew" class="new-badge">{{ t('packs.new') }}</span>
            <span class="rarity-tag" :class="current.rarity">
              {{ rarityLabel }}
            </span>
          </div>
        </div>
        <div v-if="flipped" class="sparks">
          <i v-for="spark in sparks" :key="spark.id" :style="spark.style" />
        </div>
      </div>
      <p class="hint">{{ revealHint }}</p>
    </div>

    <div v-else-if="phase === 'next-pack'" class="next-pack-stage">
      <p class="next-kicker">{{ t('packs.nextPack') }}</p>
      <div ref="stackHost" class="stack-host" />
      <p class="pack-name">{{ pack.name }}</p>
    </div>

    <div v-else-if="phase === 'stow'" class="stow-stage">
      <div ref="vaultHost" class="vault-host" />
      <div v-if="stowDone || stowKind !== 'binder'" class="stow-done">
        <h2>{{ pack.name }}</h2>
        <div class="grid">
          <div
            v-for="(pull, i) in allPulls"
            :key="`${pull.cardId}-${i}`"
            class="cell"
            :class="pull.rarity"
            :style="{ '--i': i }"
          >
            <YugiohCard :card="pull.card" size="tiny" :clickable="false" />
            <span v-if="pull.isNew" class="mini-new">{{ t('packs.new') }}</span>
            <span class="mini-rarity">{{ t(`packs.rarity.${pull.rarity}`) }}</span>
          </div>
        </div>
        <div class="actions">
          <button type="button" class="cta" @click="$emit('close')">
            {{ t('packs.done') }}
          </button>
          <router-link to="/deck-builder" class="ghost" @click="$emit('close')">
            {{ t('packs.toDeck') }}
          </router-link>
          <button
            v-if="remaining >= 1"
            type="button"
            class="ghost"
            @pointerdown.stop
            @click="$emit('open-more', 1)"
          >
            {{ t('packs.openAnother') }}
          </button>
          <button
            v-if="remaining >= 3"
            type="button"
            class="ghost"
            @pointerdown.stop
            @click="$emit('open-more', 3)"
          >
            {{ t('packs.openThree') }}
          </button>
          <button
            v-if="remaining >= 5"
            type="button"
            class="ghost"
            @pointerdown.stop
            @click="$emit('open-more', 5)"
          >
            {{ t('packs.openFive') }}
          </button>
          <button
            v-if="remaining >= 10"
            type="button"
            class="ghost"
            @pointerdown.stop
            @click="$emit('open-more', 10)"
          >
            {{ t('packs.openTen') }}
          </button>
        </div>
      </div>
    </div>

    <div v-else class="summary">
      <h2>{{ pack.name }}</h2>
      <div class="grid">
        <div
          v-for="(pull, i) in allPulls"
          :key="`${pull.cardId}-${i}`"
          class="cell"
          :class="pull.rarity"
          :style="{ '--i': i }"
        >
          <YugiohCard :card="pull.card" size="tiny" :clickable="false" />
          <span v-if="pull.isNew" class="mini-new">{{ t('packs.new') }}</span>
          <span class="mini-rarity">{{ t(`packs.rarity.${pull.rarity}`) }}</span>
        </div>
      </div>
      <div class="actions">
        <button type="button" class="cta" @click="$emit('close')">
          {{ t('packs.done') }}
        </button>
        <router-link to="/deck-builder" class="ghost" @click="$emit('close')">
          {{ t('packs.toDeck') }}
        </router-link>
        <button
          v-if="remaining >= 1"
          type="button"
          class="ghost"
          @pointerdown.stop
          @click="$emit('open-more', 1)"
        >
          {{ t('packs.openAnother') }}
        </button>
        <button
          v-if="remaining >= 3"
          type="button"
          class="ghost"
          @pointerdown.stop
          @click="$emit('open-more', 3)"
        >
          {{ t('packs.openThree') }}
        </button>
        <button
          v-if="remaining >= 5"
          type="button"
          class="ghost"
          @pointerdown.stop
          @click="$emit('open-more', 5)"
        >
          {{ t('packs.openFive') }}
        </button>
        <button
          v-if="remaining >= 10"
          type="button"
          class="ghost"
          @pointerdown.stop
          @click="$emit('open-more', 10)"
        >
          {{ t('packs.openTen') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import YugiohCard from './YugiohCard.vue'
import { CARD_BACK_URL, getLocalCardImageUrl } from '../utils/cardImages'
import { loadCardImages } from '../utils/cardImageCache'
import { type PackDef, type PackPull } from '../game/packs'
import { createStowScene, peekStowKind, type StowScene } from '../game/packs/stowScene'
import { PackStack } from '../game/packs/PackStack'
import { PackSfx } from '../game/audio/PackSfx'
import { useI18n } from '../i18n/t'
import { useCompactLayout } from '../composables/useCompactLayout'

const props = defineProps<{
  pack: PackDef
  batches: PackPull[][]
  remaining: number
}>()

defineEmits<{
  close: []
  'open-more': [qty: number]
}>()

const { t } = useI18n()
const { menusShort, menusPortrait } = useCompactLayout()
const cardBack = CARD_BACK_URL
const phase = ref<
  'tear' | 'burst' | 'reveal' | 'next-pack' | 'stow' | 'summary'
>('tear')
const batchIndex = ref(0)
const index = ref(0)
const flipped = ref(false)
const tearing = ref(false)
const ripping = ref(false)
const shaking = ref(false)
const flashing = ref(false)
const entering = ref(true)
const exiting = ref(false)
const tearProgress = ref(0)
const lastTearTick = ref(0)
const tiltX = ref(0)
const tiltY = ref(0)
const vaultHost = ref<HTMLElement | null>(null)
const stackHost = ref<HTMLElement | null>(null)
const sideStackHost = ref<HTMLElement | null>(null)
const tearHost = ref<HTMLElement | null>(null)
const sparks = ref<{ id: number; style: Record<string, string> }[]>([])
const muted = ref(PackSfx.isMuted())
const stowDone = ref(false)
const stowKind = ref<'case' | 'binder'>('case')
const inputReady = ref(false)
const timers: number[] = []
let vault: StowScene | null = null
let packStack: PackStack | null = null
let sideStack: PackStack | null = null
let alive = true
let tearOriginX = 0
let tearOriginY = 0
let tearOriginAmount = 0

const EMPTY_PULL: PackPull = {
  cardId: '',
  card: { id: '', name: '', type: '' },
  rarity: 'common',
  isNew: false,
}

const pulls = computed(
  () => props.batches[batchIndex.value] ?? props.batches[0] ?? [],
)
const allPulls = computed(() => props.batches.flat())
const current = computed(
  () => pulls.value[index.value] ?? pulls.value[0] ?? EMPTY_PULL,
)
const rarityLabel = computed(() => t(`packs.rarity.${current.value.rarity}`))
const isLast = computed(() => index.value === pulls.value.length - 1)
const hasMoreBatches = computed(
  () => batchIndex.value < props.batches.length - 1,
)
const remainingAfterCurrent = computed(
  () => Math.max(0, props.batches.length - batchIndex.value - 1),
)
const showSideStack = computed(
  () =>
    remainingAfterCurrent.value > 0 &&
    (phase.value === 'burst' || phase.value === 'reveal') &&
    !menusPortrait.value,
)
const showPackProgress = computed(
  () =>
    props.batches.length > 1 &&
    phase.value !== 'stow' &&
    phase.value !== 'summary',
)
const stackCount = computed(() =>
  Math.min(4, pulls.value.length - index.value - 1),
)
/** Fresh 3D card per pull so the last face cannot linger on a fast flip. */
const revealCardKey = computed(
  () => `${batchIndex.value}-${index.value}-${current.value.cardId}`,
)
const revealHint = computed(() => {
  if (!flipped.value) {
    return isLast.value ? t('packs.tapLast') : t('packs.tapReveal')
  }
  if (isLast.value && hasMoreBatches.value) return t('packs.tapNextPack')
  if (isLast.value) return t('packs.tapSummary')
  return t('packs.tapNext')
})
const burstCards = computed(() => {
  const radiusX = menusShort.value ? 88 : 140
  const radiusY = menusShort.value ? 48 : 90
  return Array.from({ length: 7 }, (_, i) => {
    const angle = (i / 7) * Math.PI * 2
    return {
      i,
      style: {
        '--x': `${Math.cos(angle) * radiusX}px`,
        '--y': `${Math.sin(angle) * radiusY}px`,
        '--r': `${i * 22}deg`,
        '--d': `${i * 28}ms`,
      },
    }
  })
})
const packTiltStyle = computed(() => {
  if (!tearing.value && !ripping.value) return {}
  return {
    transform: `rotateX(${tiltX.value}deg) rotateY(${tiltY.value}deg)`,
  }
})
const topClip = computed(() => ({
  clipPath: `inset(0 0 ${Math.max(0, 82 - tearProgress.value * 4)}% 0)`,
}))
const bottomClip = computed(() => ({
  clipPath: `inset(${Math.min(100, 18 + tearProgress.value * 4)}% 0 0 0)`,
}))

const later = (ms: number, fn: () => void) => {
  timers.push(window.setTimeout(fn, ms))
}

const clearTimers = () => {
  for (const id of timers) window.clearTimeout(id)
  timers.length = 0
}

const resetRevealState = () => {
  index.value = 0
  flipped.value = false
  ripping.value = false
  tearing.value = false
  tearProgress.value = 0
  lastTearTick.value = 0
  tiltX.value = 0
  tiltY.value = 0
  sparks.value = []
  entering.value = true
  exiting.value = false
  shaking.value = false
  flashing.value = false
}

const sparkCount = (rarity: PackPull['rarity']): number => {
  if (rarity === 'stormwake' || rarity === 'nightglass' || rarity === 'helios') {
    return 28
  }
  if (rarity === 'ultra') return 20
  if (rarity === 'super') return 14
  if (rarity === 'rare') return 8
  return 4
}

const sparkColor = (rarity: PackPull['rarity']): string => {
  if (rarity === 'stormwake') return '#f9a8d4'
  if (rarity === 'nightglass') return '#67e8f9'
  if (rarity === 'helios' || rarity === 'ultra' || rarity === 'super') {
    return '#fbbf24'
  }
  return '#e2e8f0'
}

const spawnSparks = (rarity: PackPull['rarity']) => {
  const count = sparkCount(rarity)
  const color = sparkColor(rarity)
  sparks.value = Array.from({ length: count }, (_, id) => {
    const angle = (Math.PI * 2 * id) / count + Math.random() * 0.4
    const dist = 70 + Math.random() * 90
    return {
      id,
      style: {
        '--x': `${Math.cos(angle) * dist}px`,
        '--y': `${Math.sin(angle) * dist}px`,
        '--d': `${0.45 + Math.random() * 0.4}s`,
        background: color,
      },
    }
  })
}

const playRevealSfx = () => {
  const rarity = current.value.rarity
  if (
    rarity === 'stormwake' ||
    rarity === 'nightglass' ||
    rarity === 'helios'
  ) {
    PackSfx.play('secret')
  } else if (rarity === 'ultra') PackSfx.play('ultra')
  else if (rarity === 'super') PackSfx.play('super')
  else if (rarity === 'rare') PackSfx.play('rare')
  else PackSfx.play('common')
}

const warmPullArt = () => {
  const list = pulls.value
  if (list.length === 0) return
  const currentId = list[index.value]?.cardId ?? list[0]!.cardId
  const currentUrl = getLocalCardImageUrl(currentId)
  const rest = list
    .map((pull) => getLocalCardImageUrl(pull.cardId))
    .filter((url) => url !== currentUrl)
  void loadCardImages([currentUrl, ...rest])
}

watch(
  () =>
    `${batchIndex.value}:${index.value}:${pulls.value.map((p) => p.cardId).join(',')}`,
  () => warmPullArt(),
  { immediate: true },
)

const enterBurst = () => {
  disposePackStack()
  disposeSideStack()
  tearing.value = false
  ripping.value = false
  phase.value = 'burst'
  PackSfx.play('burst')
  later(720, () => {
    phase.value = 'reveal'
    inputReady.value = true
    entering.value = true
    flipped.value = false
    PackSfx.play('slide')
    void nextTick(() => startSideStack())
    later(380, () => {
      entering.value = false
    })
  })
}

const finishTear = () => {
  if (!inputReady.value) return
  if (phase.value !== 'tear' || ripping.value) return
  ripping.value = true
  packStack?.rip()
  tearProgress.value = 1
  PackSfx.play('rip')
  later(620, () => {
    if (!alive) return
    enterBurst()
  })
}

const revealCurrent = () => {
  if (flipped.value) return
  flipped.value = true
  playRevealSfx()
  spawnSparks(current.value.rarity)
  const rareHit =
    current.value.rarity === 'ultra' ||
    current.value.rarity === 'helios' ||
    current.value.rarity === 'nightglass' ||
    current.value.rarity === 'stormwake'
  if (rareHit) {
    shaking.value = true
    flashing.value = true
    later(160, () => {
      flashing.value = false
    })
    later(520, () => {
      shaking.value = false
    })
  }
}

const disposeVault = () => {
  vault?.dispose()
  vault = null
}

const disposePackStack = () => {
  packStack?.dispose()
  packStack = null
}

const disposeSideStack = () => {
  sideStack?.dispose()
  sideStack = null
}

const showSummary = () => {
  disposeVault()
  phase.value = 'summary'
  PackSfx.play('summary')
}

const startVault = () => {
  disposeVault()
  const host = vaultHost.value
  if (!host) {
    showSummary()
    return
  }
  try {
    const kind = peekStowKind(host)
    stowKind.value = kind
    vault = createStowScene(
      host,
      allPulls.value.map((pull) => getLocalCardImageUrl(pull.cardId)),
      {
        onCardLand: () => PackSfx.play('stow'),
        onLidClose: () => PackSfx.play('lid'),
        onContextLost: () => {
          window.setTimeout(() => {
            if (!alive || phase.value !== 'stow') return
            showSummary()
          }, 0)
        },
      },
      kind === 'binder' ? t('packs.binderLabel') : t('packs.vaultLabel'),
    )
    void vault.play().then(() => {
      if (!alive || phase.value !== 'stow') return
      stowDone.value = true
      PackSfx.play('summary')
    })
  } catch {
    showSummary()
  }
}

const showStow = () => {
  disposePackStack()
  disposeSideStack()
  stowKind.value = peekStowKind()
  phase.value = 'stow'
  stowDone.value = false
  PackSfx.play('whoosh')
  void nextTick(() => startVault())
}

const startSideStack = () => {
  disposeSideStack()
  const host = sideStackHost.value
  const left = remainingAfterCurrent.value
  if (!host || left < 1) return
  try {
    sideStack = new PackStack(host, props.pack.cover, left, 'idle', {
      onContextLost: () => {
        window.setTimeout(() => disposeSideStack(), 0)
      },
    })
    void sideStack.play()
  } catch {
    disposeSideStack()
  }
}

const startHoldTear = () => {
  inputReady.value = false
  ripping.value = false
  tearProgress.value = 0
  lastTearTick.value = 0
  disposeSideStack()
  disposePackStack()
  phase.value = 'tear'
  void nextTick(() => {
    const host = tearHost.value
    const left = props.batches.length - batchIndex.value
    if (!host || left < 1) {
      enterBurst()
      return
    }
    try {
      packStack = new PackStack(host, props.pack.cover, left, 'hold', {
        onContextLost: () => {
          window.setTimeout(() => {
            if (!alive || phase.value !== 'tear') return
            enterBurst()
          }, 0)
        },
      })
      void packStack.play()
      later(320, () => {
        if (alive) inputReady.value = true
      })
    } catch {
      later(280, () => {
        if (alive) inputReady.value = true
      })
    }
  })
}

const startPackPresent = (advance: boolean) => {
  inputReady.value = false
  if (advance) {
    batchIndex.value += 1
    resetRevealState()
  }
  disposeSideStack()
  disposePackStack()
  phase.value = 'next-pack'
  PackSfx.play('whoosh')
  void nextTick(() => {
    const host = stackHost.value
    const left = props.batches.length - batchIndex.value
    if (!host || left < 1) {
      enterBurst()
      return
    }
    try {
      packStack = new PackStack(
        host,
        props.pack.cover,
        left,
        'draw',
        {
          onLift: () => PackSfx.play('whoosh'),
          onOpen: () => PackSfx.play('burst'),
          onContextLost: () => {
            window.setTimeout(() => {
              if (!alive || phase.value !== 'next-pack') return
              enterBurst()
            }, 0)
          },
        },
      )
      void packStack.play().then(() => {
        if (!alive || phase.value !== 'next-pack') return
        enterBurst()
      })
    } catch {
      enterBurst()
    }
  })
}

const startNextPack = () => {
  startPackPresent(true)
}

const nextCard = () => {
  if (isLast.value) {
    if (hasMoreBatches.value) {
      exiting.value = true
      PackSfx.play('slide')
      later(220, () => {
        exiting.value = false
        startNextPack()
      })
      return
    }
    showStow()
    return
  }
  exiting.value = true
  PackSfx.play('slide')
  later(220, () => {
    flipped.value = false
    sparks.value = []
    shaking.value = false
    flashing.value = false
    index.value += 1
    exiting.value = false
    entering.value = true
    later(320, () => {
      entering.value = false
    })
  })
}

const advanceReveal = () => {
  if (phase.value !== 'reveal' || exiting.value) return
  if (!flipped.value) revealCurrent()
  else nextCard()
}

const skip = () => {
  if (phase.value === 'stow') {
    vault?.skip()
    return
  }
  clearTimers()
  disposePackStack()
  disposeSideStack()
  PackSfx.play('skip')
  showStow()
}

const toggleSfx = () => {
  muted.value = PackSfx.toggle()
  if (!muted.value) PackSfx.play('whoosh')
}

const updateTear = (event: PointerEvent) => {
  const host = tearHost.value
  if (!host) return
  const rect = host.getBoundingClientRect()
  const spanX = Math.max(140, rect.width * 0.42)
  const spanY = Math.max(140, rect.height * 0.38)
  const dx = (event.clientX - tearOriginX) / spanX
  const dy = (tearOriginY - event.clientY) / spanY
  const next = Math.min(1, Math.max(0, tearOriginAmount + Math.max(dx, dy)))
  tearProgress.value = next
  packStack?.setTear(next)
  if (next - lastTearTick.value >= 0.12) {
    lastTearTick.value = next
    PackSfx.play('tear')
  }
}

const releaseCapture = (event: PointerEvent) => {
  if (event.currentTarget instanceof HTMLElement) {
    try {
      event.currentTarget.releasePointerCapture(event.pointerId)
    } catch {
      /* already released */
    }
  }
}

const onPointerDown = (event: PointerEvent) => {
  if (phase.value === 'tear') {
    const host = tearHost.value
    if (!inputReady.value || !host || ripping.value) return
    if (!(event.target instanceof Node) || !host.contains(event.target)) return
    tearing.value = true
    tearOriginX = event.clientX
    tearOriginY = event.clientY
    tearOriginAmount = tearProgress.value
    if (event.currentTarget instanceof HTMLElement) {
      event.currentTarget.setPointerCapture(event.pointerId)
    }
    PackSfx.play('tear')
    return
  }
  if (phase.value === 'reveal') advanceReveal()
}

const onPointerMove = (event: PointerEvent) => {
  if (!inputReady.value || phase.value !== 'tear' || ripping.value) return
  if (!tearing.value) return
  updateTear(event)
  if (tearProgress.value >= 0.86) finishTear()
}

const onPointerUp = (event: PointerEvent) => {
  tearing.value = false
  releaseCapture(event)
  if (!inputReady.value || phase.value !== 'tear' || ripping.value) return
  if (tearProgress.value >= 0.82) finishTear()
  else {
    tearProgress.value = 0
    lastTearTick.value = 0
    packStack?.setTear(0)
  }
}

onMounted(() => {
  alive = true
  muted.value = PackSfx.isMuted()
  void nextTick(() => startHoldTear())
})

onUnmounted(() => {
  alive = false
  for (const id of timers) window.clearTimeout(id)
  disposePackStack()
  disposeSideStack()
  disposeVault()
})
</script>

<style scoped>
.opener {
  position: fixed;
  inset: 0;
  z-index: 10100;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: #e2e8f0;
  user-select: none;
  touch-action: none;
  background:
    radial-gradient(circle at 50% 18%, rgba(250, 204, 21, 0.16), transparent 42%),
    radial-gradient(circle at 50% 80%, rgba(30, 58, 138, 0.35), transparent 50%),
    #070714;
  animation: room-in 0.45s ease;
}

.opener.mood-ultra,
.opener.mood-helios.reveal {
  background:
    radial-gradient(circle at 50% 20%, rgba(251, 191, 36, 0.28), transparent 45%),
    #070714;
}

.opener.mood-helios.reveal {
  background:
    radial-gradient(circle at 50% 20%, rgba(255, 170, 40, 0.32), transparent 45%),
    #070714;
}

.opener.mood-nightglass.reveal {
  background:
    radial-gradient(circle at 50% 30%, rgba(56, 189, 248, 0.24), transparent 40%),
    radial-gradient(circle at 70% 70%, rgba(167, 139, 250, 0.2), transparent 45%),
    #070714;
}

.opener.mood-stormwake.reveal {
  background:
    radial-gradient(circle at 50% 30%, rgba(244, 114, 182, 0.22), transparent 40%),
    radial-gradient(circle at 70% 70%, rgba(34, 211, 238, 0.18), transparent 45%),
    #070714;
}

.vignette {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: radial-gradient(circle at center, transparent 40%, rgba(0, 0, 0, 0.55));
}

.flash {
  position: absolute;
  inset: 0;
  pointer-events: none;
  animation: flash 0.18s ease;
  background: rgba(255, 255, 255, 0.35);
}

.flash.stormwake,
.flash.nightglass {
  background: rgba(244, 114, 182, 0.28);
}

.flash.helios {
  background: rgba(251, 191, 36, 0.28);
}

.pack-progress {
  position: absolute;
  top: 22px;
  left: 18px;
  z-index: 3;
  color: #fde68a;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.hud {
  position: absolute;
  top: 18px;
  right: 18px;
  z-index: 3;
  display: flex;
  gap: 8px;
}

.sfx,
.skip {
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid #64748b;
  background: rgba(15, 23, 42, 0.7);
  color: #fff;
  font-weight: 600;
  cursor: pointer;
}

.tear-stage,
.reveal-stage,
.burst-stage,
.next-pack-stage,
.summary {
  position: relative;
  z-index: 1;
  width: min(920px, 100%);
  padding: 24px;
  text-align: center;
}

.stow-stage {
  position: absolute;
  inset: 0;
  z-index: 1;
  padding: 72px 16px 16px;
}

.vault-host {
  position: absolute;
  inset: 0;
}

.stow-done {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  padding: 16px 20px 28px;
  pointer-events: auto;
  background: linear-gradient(transparent, rgba(7, 7, 20, 0.94) 28%);
}

.stow-done h2 {
  color: #fff;
  font-size: 22px;
}

.stow-done .grid {
  max-height: 22vh;
}

.hint {
  color: #e2e8f0;
  margin-bottom: 16px;
}

.scene {
  height: 360px;
  perspective: 1100px;
  perspective-origin: 50% 42%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pack-3d {
  position: relative;
  width: 200px;
  height: 300px;
  transform-style: preserve-3d;
  transform-origin: center center;
  transition: transform 0.12s ease-out;
  filter: drop-shadow(0 22px 36px rgba(0, 0, 0, 0.6));
  animation: idle-orbit 4.8s ease-in-out infinite;
}

.pack-3d.tearing,
.pack-3d.ripping {
  animation: none;
}

.pack-3d.ripping {
  animation: pack-fall 0.55s ease-in forwards;
}

.box-face {
  position: absolute;
  inset: 0;
  backface-visibility: hidden;
}

.front-face {
  overflow: hidden;
  border-radius: 4px;
  transform: translateZ(10px);
}

.back-face {
  background: linear-gradient(180deg, #1e293b, #0f172a);
  transform: rotateY(180deg) translateZ(10px);
}

.left-face,
.right-face {
  width: 20px;
  left: 90px;
  background: linear-gradient(90deg, #6b5214, #d4af37 55%, #8a7018);
}

.left-face {
  transform: rotateY(-90deg) translateZ(100px);
}

.right-face {
  transform: rotateY(90deg) translateZ(100px);
}

.top-face,
.bottom-face {
  height: 20px;
  top: 140px;
  background: #c9a227;
}

.top-face {
  transform: rotateX(90deg) translateZ(150px);
}

.bottom-face {
  transform: rotateX(-90deg) translateZ(150px);
}

.pack-shine {
  position: absolute;
  inset: 0;
  z-index: 3;
  pointer-events: none;
  background: linear-gradient(
    110deg,
    transparent 30%,
    rgba(255, 255, 255, 0.28) 48%,
    transparent 62%
  );
  background-size: 220% 100%;
  animation: shine 2.4s linear infinite;
}

.pack-half {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

.pack-half img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
}

.pack-wrap.ripping .pack-half.top {
  animation: fly-up 0.5s cubic-bezier(0.2, 0.7, 0.2, 1) forwards;
}

.pack-wrap.ripping .pack-half.bottom {
  animation: fly-down 0.5s cubic-bezier(0.2, 0.7, 0.2, 1) forwards;
}

.tear-track {
  position: absolute;
  left: 7%;
  right: 7%;
  top: 16%;
  height: 11px;
  z-index: 4;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.16);
  box-shadow: 0 0 18px rgba(250, 204, 21, 0.4);
}

.tear-fill {
  height: 100%;
  width: calc(var(--progress) * 100%);
  border-radius: inherit;
  background: linear-gradient(90deg, #fde68a, #f59e0b);
}

.tear-spark {
  position: absolute;
  top: -6px;
  left: calc(var(--progress) * 100%);
  width: 18px;
  height: 22px;
  margin-left: -9px;
  border-radius: 50%;
  background: #fff7c2;
  box-shadow: 0 0 16px 6px rgba(251, 191, 36, 0.8);
  opacity: 0.9;
}

.pack-name {
  font-size: 22px;
  color: #fff;
  margin-bottom: 10px;
}

.ghost-open,
.ghost,
.cta {
  display: inline-block;
  padding: 10px 18px;
  border-radius: 10px;
  font-weight: 700;
  cursor: pointer;
}

.ghost-open,
.ghost {
  border: 1px solid #64748b;
  background: transparent;
  color: #e2e8f0;
}

.ghost-open:disabled {
  opacity: 0.45;
  cursor: default;
}

.cta {
  border: none;
  background: #ca8a04;
  color: #111;
}

.burst-stage {
  height: 360px;
}

.burst-card {
  position: absolute;
  left: 50%;
  top: 42%;
  width: 78px;
  height: 114px;
  margin: -57px 0 0 -39px;
  border-radius: 6px;
  object-fit: cover;
  animation: burst-out 0.7s ease-out forwards;
  animation-delay: var(--d);
}

.count {
  color: #fbbf24;
  letter-spacing: 0.12em;
  margin-bottom: 12px;
}

.stack {
  position: relative;
  height: 0;
}

.stack span {
  position: absolute;
  left: 50%;
  width: 220px;
  height: 12px;
  margin-left: -110px;
  border-radius: 4px;
  background: #1e293b;
  transform: translateY(calc(var(--s) * 5px));
  opacity: 0.35;
}

.card-stage {
  position: relative;
  width: 240px;
  height: 350px;
  margin: 8px auto 16px;
  perspective: 1400px;
  transform-style: preserve-3d;
}

.card-stage.foil-rare {
  box-shadow: 0 0 12px 2px rgba(203, 213, 225, 0.55);
}

.card-stage.foil-super {
  box-shadow: 0 0 14px 3px rgba(250, 204, 21, 0.55);
}

.card-stage.foil-ultra {
  box-shadow:
    0 0 18px 4px rgba(251, 191, 36, 0.92),
    0 0 32px 8px rgba(180, 83, 9, 0.45);
}

.card-stage.foil-helios {
  box-shadow:
    0 0 18px 4px rgba(251, 191, 36, 0.85),
    0 0 36px 8px rgba(255, 140, 20, 0.5);
}

.card-stage.foil-nightglass {
  box-shadow:
    0 0 18px 4px rgba(125, 211, 252, 0.95),
    0 0 36px 9px rgba(139, 92, 246, 0.55);
}

.card-stage.foil-stormwake {
  box-shadow:
    0 0 20px 5px rgba(244, 114, 182, 0.95),
    0 0 36px 10px rgba(34, 211, 238, 0.55);
}

.card-stage.entering {
  animation: deal-in 0.38s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.card-stage.exiting {
  animation: deal-out 0.22s ease-in forwards;
}

.card-stage.last.flipped {
  animation: last-pop 0.45s ease;
}

.card-3d {
  position: relative;
  width: 100%;
  height: 100%;
  transform-style: preserve-3d;
  transition: transform 0.62s cubic-bezier(0.18, 0.85, 0.22, 1);
}

.card-stage.entering .card-3d {
  transition: none;
}

.card-3d.flipped {
  transform: rotateY(180deg) scale(1.04);
}

.face {
  position: absolute;
  inset: 0;
  backface-visibility: hidden;
  border-radius: 10px;
  overflow: hidden;
}

.face.back img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.face.front {
  transform: rotateY(180deg);
  transform-style: flat;
  isolation: isolate;
  overflow: hidden;
}

/* YugiohCard large is 240px; compact stages are smaller. Fill the stage
   so the right edge of the card is not clipped. */
.face.front :deep(.yugioh-card-container) {
  width: 100% !important;
  height: 100% !important;
}

.face.front :deep(.yugioh-card-simple),
.face.front :deep(.complete-card-container) {
  width: 100%;
  height: 100%;
}

.foil-sweep {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
    105deg,
    transparent 35%,
    rgba(255, 255, 255, 0.34) 50%,
    transparent 65%
  );
  background-size: 240% 100%;
  animation: shine 1.1s ease 0.15s both;
}

.sparks {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.sparks i {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  animation: spark var(--d) ease-out forwards;
}

.shake {
  animation: shake 0.48s ease;
}

.new-badge,
.mini-new {
  position: absolute;
  top: 8px;
  left: 8px;
  padding: 3px 7px;
  border-radius: 999px;
  background: #16a34a;
  color: #fff;
  font-size: 11px;
  font-weight: 800;
}

.rarity-tag,
.mini-rarity {
  position: absolute;
  right: 8px;
  bottom: 8px;
  padding: 3px 7px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  background: rgba(0, 0, 0, 0.65);
}

.rarity-tag.common,
.cell.common .mini-rarity {
  color: #94a3b8;
}

.rarity-tag.rare,
.cell.rare .mini-rarity {
  color: #e2e8f0;
}

.rarity-tag.super,
.cell.super .mini-rarity {
  color: #fbbf24;
}

.rarity-tag.ultra,
.cell.ultra .mini-rarity {
  color: #f59e0b;
}

.rarity-tag.helios,
.cell.helios .mini-rarity {
  color: #fbbf24;
}

.rarity-tag.nightglass,
.cell.nightglass .mini-rarity {
  color: #67e8f9;
}

.rarity-tag.stormwake,
.cell.stormwake .mini-rarity {
  color: #f472b6;
}

.grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  max-height: 58vh;
  overflow: auto;
  margin: 18px 0;
}

.cell {
  position: relative;
  animation: pop-in 0.38s ease backwards;
  animation-delay: calc(var(--i) * 45ms);
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
}

.tear-stage {
  width: min(1100px, 100%);
  height: min(82vh, 760px);
}

.tear-stage .hint,
.tear-stage .pack-name,
.tear-stage .ghost-open {
  position: relative;
  z-index: 2;
}

.tear-host {
  position: absolute;
  inset: 48px 8px 92px;
  cursor: grab;
}

.opener.tear:active .tear-host {
  cursor: grabbing;
}

.next-pack-stage {
  width: min(1100px, 100%);
  height: min(82vh, 760px);
}

.next-kicker {
  position: relative;
  z-index: 2;
  color: #fde68a;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  font-size: 14px;
  margin-bottom: 8px;
}

.stack-host {
  position: absolute;
  inset: 8px 8px 44px;
}

.next-pack-stage .pack-name {
  position: relative;
  z-index: 2;
  margin-top: 8px;
}

.side-stack-host {
  position: absolute;
  left: 12px;
  bottom: 72px;
  z-index: 2;
  width: min(200px, 28vw);
  height: min(260px, 36vh);
  pointer-events: none;
}

@keyframes room-in {
  from {
    opacity: 0;
    transform: scale(1.04);
  }
}

@keyframes idle-orbit {
  0%,
  100% {
    transform: rotateY(-24deg) rotateX(8deg) translateY(0);
  }
  50% {
    transform: rotateY(24deg) rotateX(2deg) translateY(-10px);
  }
}

@keyframes pack-fall {
  to {
    transform: rotateX(-55deg) translateZ(-40px) translateY(90px) scale(0.72);
    opacity: 0;
  }
}

@keyframes shine {
  from {
    background-position: 120% 0;
  }
  to {
    background-position: -20% 0;
  }
}

@keyframes fly-up {
  to {
    transform: translateY(-130%) rotate(-12deg);
    opacity: 0;
  }
}

@keyframes fly-down {
  to {
    transform: translateY(130%) rotate(10deg);
    opacity: 0;
  }
}

@keyframes burst-out {
  0% {
    transform: translate(0, 0) scale(0.4) rotate(0deg);
    opacity: 0;
  }
  40% {
    opacity: 1;
  }
  100% {
    transform: translate(var(--x), var(--y)) scale(0.85) rotate(var(--r));
    opacity: 0;
  }
}

@keyframes deal-in {
  from {
    transform: translateY(40px) scale(0.86);
    opacity: 0;
  }
}

@keyframes deal-out {
  to {
    transform: translateX(-80px) rotate(-8deg);
    opacity: 0;
  }
}

@keyframes last-pop {
  50% {
    transform: scale(1.06);
  }
}

@keyframes spark {
  to {
    transform: translate(var(--x), var(--y)) scale(0.2);
    opacity: 0;
  }
}

@keyframes flash {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

@keyframes shake {
  0%,
  100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-7px);
  }
  75% {
    transform: translateX(7px);
  }
}

@keyframes pop-in {
  from {
    transform: translateY(12px) scale(0.8);
    opacity: 0;
  }
}

@media (max-height: 500px) and (max-width: 1100px) {
  .hud {
    top: max(8px, env(safe-area-inset-top, 0px));
    right: max(8px, env(safe-area-inset-right, 0px));
  }

  .pack-progress {
    top: max(8px, env(safe-area-inset-top, 0px));
    left: max(12px, env(safe-area-inset-left, 0px));
  }

  .tear-stage,
  .reveal-stage,
  .burst-stage,
  .next-pack-stage,
  .summary {
    padding: 8px 12px;
  }

  .tear-stage,
  .next-pack-stage {
    height: min(100dvh, 760px);
  }

  .tear-host {
    inset: 28px 8px 56px;
  }

  .burst-stage {
    height: min(240px, 68dvh);
  }

  .burst-card {
    width: 56px;
    height: 82px;
    margin: -41px 0 0 -28px;
  }

  .card-stage {
    width: min(190px, 42vh);
    height: auto;
    aspect-ratio: 240 / 350;
    margin: 4px auto 8px;
  }

  .grid {
    max-height: 36vh;
    margin: 8px 0;
    gap: 8px;
  }

  .stow-done {
    max-height: 100%;
    overflow-y: auto;
    padding: 8px 12px 16px;
  }

  .stow-done .grid {
    max-height: 28vh;
  }

  .actions {
    padding-bottom: env(safe-area-inset-bottom, 0px);
  }
}

@media (max-width: 720px) {
  .card-stage {
    width: min(220px, 70vw);
    height: auto;
    aspect-ratio: 240 / 350;
  }

  .tear-stage,
  .reveal-stage,
  .next-pack-stage {
    width: 100%;
    padding: 12px;
  }
}

@media (max-width: 720px) and (orientation: portrait) {
  .opener.tear,
  .opener.next-pack,
  .opener.reveal {
    align-items: stretch;
  }

  .tear-stage,
  .next-pack-stage {
    width: 100%;
    height: 100%;
    padding: 0;
  }

  .tear-host {
    inset: max(56px, env(safe-area-inset-top, 0px)) 0
      max(96px, env(safe-area-inset-bottom, 0px));
  }

  .stack-host {
    inset: max(40px, env(safe-area-inset-top, 0px)) 0
      max(52px, env(safe-area-inset-bottom, 0px));
  }

  .tear-stage .hint,
  .next-kicker {
    margin-top: max(52px, env(safe-area-inset-top, 0px));
  }

  .card-stage {
    width: min(240px, 72vw);
    margin: 8px auto 12px;
  }

  .reveal-stage {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: 64px 12px 24px;
  }

  .side-stack-host {
    left: 4px;
    bottom: max(64px, env(safe-area-inset-bottom, 0px));
    width: min(108px, 24vw);
    height: min(140px, 18vh);
  }

  .stow-stage {
    padding: 0;
  }
}
</style>
